/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    type DataChangedCallback,
    type MutableValue,
    type ObservableValue,
    type Picked,
} from '@amazon/vinyl-observable'
import { globalRef, type Unsubscribe } from '@amazon/vinyl-util'

/**
 * A module-scope observable whose backing value lives in a `globalRef`, so a
 * global-registry reset (between tests) starts it over from `init`. The
 * returned view is a stable binding to export; each read, write or
 * subscription goes to the value current at that moment, and `map` / `pick`
 * views follow it the same way.
 */
export interface GlobalData<T> extends MutableValue<T> {
    /** Build the backing value now rather than on first use. */
    initialize(): void
}

export function globalData<T>(init: () => MutableValue<T>): GlobalData<T> {
    const ref = globalRef(init)
    const view = new RebindingValue(() => ref.value)
    return Object.assign(view, { initialize: () => ref.initialize() })
}

class RebindingValue<T> implements MutableValue<T> {
    constructor(private readonly resolve: () => MutableValue<T>) {}

    get value(): T {
        return this.resolve().value
    }

    set value(value: T) {
        this.resolve().value = value
    }

    get changeId(): number {
        return this.resolve().changeId
    }

    getValue(): T {
        return this.resolve().value
    }

    setValue(value: T): void {
        this.resolve().value = value
    }

    onData(callback: DataChangedCallback<T>): Unsubscribe {
        return this.resolve().onData(callback)
    }

    invalidate(): void {
        this.resolve().invalidate()
    }

    map<U>(transform: (value: T) => U): ObservableValue<U>
    map<U>(transform: (value: T) => U, setData: (transformed: U, original: T) => T): MutableValue<U>
    map<U>(
        transform: (value: T) => U,
        setData?: (transformed: U, original: T) => T
    ): MutableValue<U> {
        return new RebindingValue(
            this.derived((source) =>
                setData
                    ? source.map(transform, setData)
                    : (source.map(transform) as MutableValue<U>)
            )
        )
    }

    pick<K extends keyof NonNullable<T>>(prop: K): MutableValue<Picked<T, K>>
    pick(prop: PropertyKey): MutableValue<unknown>
    pick(prop: PropertyKey): MutableValue<unknown> {
        return new RebindingValue(this.derived((source) => source.pick(prop)))
    }

    // One derived view per backing value, so a view keeps its own change
    // caching and is rebuilt only when the backing value is replaced.
    private derived<U>(
        derive: (source: MutableValue<T>) => MutableValue<U>
    ): () => MutableValue<U> {
        let source: MutableValue<T> | undefined
        let view: MutableValue<U> | undefined
        return () => {
            const current = this.resolve()
            if (current !== source || !view) {
                source = current
                view = derive(current)
            }
            return view
        }
    }
}
